import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  uploadBytesResumable,
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

// Detectar si estamos en desarrollo local
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const isProductionDomain = window.location.hostname === 'tienda.vinogenes.es' || 
                          window.location.hostname === 'vinogenes-5207a.web.app' ||
                          window.location.hostname === 'vinogenes-5207a.firebaseapp.com';

// Función helper para convertir archivo a Data URL (para desarrollo local)
const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Función helper para subir imágenes con mejor manejo de CORS
const uploadImageToStorage = async (file, path) => {
  // Debug: Mostrar información del dominio actual
  console.log('🔍 Debug - Hostname actual:', window.location.hostname);
  console.log('🔍 Debug - isProductionDomain:', isProductionDomain);
  console.log('🔍 Debug - isDevelopment:', isDevelopment);
  console.log('🔍 Debug - isLocalhost:', isLocalhost);

  // Si estamos en el dominio de producción, usar SOLO Data URL (sin intentar Firebase Storage)
  if (isProductionDomain) {
    console.log('🌐 Dominio de producción detectado, usando SOLO Data URL...');
    try {
      const dataURL = await fileToDataURL(file);
      console.log('📸 Imagen guardada como Data URL en producción');
      return dataURL;
    } catch (dataURLError) {
      console.error('❌ Error creando Data URL en producción:', dataURLError);
      throw new Error('No se pudo procesar la imagen en producción');
    }
  }

  // Si estamos en desarrollo local, usar Data URL como alternativa temporal
  if (isDevelopment && isLocalhost) {
    try {
      console.log('🏠 Modo desarrollo local detectado, usando Data URL temporal...');
      const dataURL = await fileToDataURL(file);
      console.log('📸 Imagen convertida a Data URL para desarrollo local');
      return dataURL;
    } catch (error) {
      console.warn('⚠️ Error creando Data URL, intentando Firebase Storage...', error);
    }
  }

  try {
    console.log('📸 Subiendo imagen a Firebase Storage...', path);
    
    const imageRef = ref(storage, path);
    
    // Usar uploadBytesResumable para mejor compatibilidad con CORS
    const uploadTask = uploadBytesResumable(imageRef, file);
    
    // Esperar a que la subida termine
    await new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('📊 Progreso de subida:', progress + '%');
        },
        (error) => {
          console.error('❌ Error durante la subida:', error);
          reject(error);
        },
        () => {
          console.log('✅ Subida completada');
          resolve();
        }
      );
    });
    
    // Obtener la URL de descarga
    const downloadURL = await getDownloadURL(imageRef);
    console.log('📸 URL de imagen obtenida:', downloadURL);
    
    return downloadURL;
    
  } catch (error) {
    console.error('❌ Error subiendo imagen:', error);
    
    // Detectar errores de CORS y de red
    const isCorsError = error.code === 'storage/cors-error' || 
                       error.message.includes('CORS') ||
                       error.message.includes('ERR_FAILED') ||
                       error.message.includes('blocked by CORS policy');
    
    // Si falla con uploadBytesResumable, intentar con uploadBytes
    if (isCorsError) {
      console.log('⚠️ Error CORS detectado, usando método alternativo...');
      try {
        const uploadResult = await uploadBytes(ref(storage, path), file);
        const downloadURL = await getDownloadURL(uploadResult.ref);
        console.log('✅ Subida alternativa exitosa:', downloadURL);
        return downloadURL;
      } catch (fallbackError) {
        console.error('❌ Error en método alternativo:', fallbackError);
        
        // Detectar si el error alternativo también es de CORS
        const isFallbackCorsError = fallbackError.code === 'storage/cors-error' || 
                                   fallbackError.message.includes('CORS') ||
                                   fallbackError.message.includes('ERR_FAILED') ||
                                   fallbackError.message.includes('blocked by CORS policy');
        
        // Si estamos en producción y hay error de CORS, usar Data URL temporal
        if (isProductionDomain && (isCorsError || isFallbackCorsError)) {
          console.log('🌐 Error CORS en producción, usando Data URL temporal...');
          try {
            const dataURL = await fileToDataURL(file);
            console.log('📸 Imagen guardada como Data URL temporal en producción');
            return dataURL;
          } catch (dataURLError) {
            console.error('❌ Error creando Data URL en producción:', dataURLError);
          }
        }
        
        // Como último recurso en desarrollo, usar Data URL
        if (isDevelopment) {
          console.log('🏠 Usando Data URL como último recurso en desarrollo...');
          try {
            const dataURL = await fileToDataURL(file);
            console.log('📸 Imagen guardada como Data URL para desarrollo');
            return dataURL;
          } catch (dataURLError) {
            console.error('❌ Error creando Data URL:', dataURLError);
          }
        }
        
        throw new Error(`Error subiendo imagen: ${fallbackError.message}. Esto puede ser un problema de CORS. Ejecuta setup-cors.bat para solucionarlo.`);
      }
    }
    
    throw error;
  }
};

// Helper para generar IDs únicos
const generateId = () => Date.now().toString();

// Servicio para Vinos
export const firebaseWinesService = {
  // Obtener todos los vinos
  getAll: async () => {
    try {
      const winesRef = collection(db, 'wines');
      const q = query(winesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const wines = [];
      querySnapshot.forEach((doc) => {
        wines.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return { data: { data: wines } };
    } catch (error) {
      console.error('Error fetching wines:', error);
      throw error;
    }
  },

  // Crear un nuevo vino
  create: async (formData) => {
    try {
      let imageUrl = null;
      
      // Subir imagen si existe
      if (formData.get('image')) {
        const imageFile = formData.get('image');
        imageUrl = await uploadImageToStorage(imageFile, `wines/${generateId()}_${imageFile.name}`);
        console.log('📸 Imagen subida a Firebase Storage:', imageUrl);
      }

      const wineData = {
        name: formData.get('name'),
        description: formData.get('description'),
        price: parseFloat(formData.get('price')),
        categoryId: formData.get('categoryId'),
        wineryId: formData.get('wineryId'),
        type: formData.get('type'),
        year: formData.get('year') ? parseInt(formData.get('year')) : null,
        alcoholContent: formData.get('alcoholContent') ? parseFloat(formData.get('alcoholContent')) : null,
        stock: formData.get('stock') ? parseInt(formData.get('stock')) : 0,
        weightKg: formData.get('weightKg') ? parseFloat(formData.get('weightKg')) : null,
        imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('🍷 Creando vino en Firestore:', wineData);

      const winesRef = collection(db, 'wines');
      const docRef = await addDoc(winesRef, wineData);
      
      console.log('✅ Vino guardado en Firestore con ID:', docRef.id);
      
      return { 
        data: { 
          success: true, 
          data: { id: docRef.id, ...wineData } 
        } 
      };
    } catch (error) {
      console.error('❌ Error creating wine:', error);
      throw error;
    }
  },

  // Actualizar un vino
  update: async (id, formData) => {
    try {
      console.log('🔄 Actualizando vino en Firestore:', id);
      
      const wineRef = doc(db, 'wines', id);
      const wineSnap = await getDoc(wineRef);
      
      if (!wineSnap.exists()) {
        throw new Error('Vino no encontrado');
      }

      const currentData = wineSnap.data();
      let imageUrl = currentData.imageUrl;

      // Subir nueva imagen si se proporcionó
      if (formData.get('image')) {
        console.log('📸 Subiendo nueva imagen...');
        
        // Eliminar imagen anterior si existe y es una URL de Firebase Storage
        if (currentData.imageUrl && currentData.imageUrl.includes('firebasestorage.googleapis.com')) {
          try {
            // Extraer la ruta del archivo de la URL completa
            const imageUrl = currentData.imageUrl;
            const decodedUrl = decodeURIComponent(imageUrl);
            const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
            
            if (pathMatch && pathMatch[1]) {
              const imagePath = pathMatch[1];
              const oldImageRef = ref(storage, imagePath);
              await deleteObject(oldImageRef);
              console.log('🗑️ Imagen anterior eliminada:', imagePath);
            }
          } catch (error) {
            console.warn('⚠️ Error deleting old image:', error);
            // No fallar la actualización solo por no poder eliminar la imagen anterior
          }
        }

        const imageFile = formData.get('image');
        const imageRef = ref(storage, `wines/${generateId()}_${imageFile.name}`);
        imageUrl = await uploadImageToStorage(imageFile, `wines/${generateId()}_${imageFile.name}`);
        console.log('📸 Nueva imagen subida:', imageUrl);
      }

      const updateData = {
        name: formData.get('name'),
        description: formData.get('description'),
        price: parseFloat(formData.get('price')),
        categoryId: formData.get('categoryId'),
        wineryId: formData.get('wineryId'),
        type: formData.get('type'),
        year: formData.get('year') ? parseInt(formData.get('year')) : null,
        alcoholContent: formData.get('alcoholContent') ? parseFloat(formData.get('alcoholContent')) : null,
        stock: formData.get('stock') ? parseInt(formData.get('stock')) : 0,
        weightKg: formData.get('weightKg') ? parseFloat(formData.get('weightKg')) : null,
        imageUrl,
        updatedAt: serverTimestamp()
      };

      console.log('📝 Datos de actualización:', updateData);

      await updateDoc(wineRef, updateData);
      
      console.log('✅ Vino actualizado en Firestore:', id);
      
      return { 
        data: { 
          success: true, 
          data: { id, ...updateData } 
        } 
      };
    } catch (error) {
      console.error('❌ Error updating wine:', error);
      throw error;
    }
  },

  // Eliminar un vino
  delete: async (id) => {
    try {
      console.log('🗑️ Eliminando vino de Firestore:', id);
      
      const wineRef = doc(db, 'wines', id);
      const wineSnap = await getDoc(wineRef);
      
      if (!wineSnap.exists()) {
        throw new Error('Vino no encontrado');
      }

      const wineData = wineSnap.data();
      
      // No eliminar la imagen - mantenerla en Firebase Storage
      console.log('📸 Imagen mantenida en Firebase Storage (no se elimina)');

      await deleteDoc(wineRef);
      
      console.log('✅ Vino eliminado de Firestore:', id);
      
      return { data: { success: true } };
    } catch (error) {
      console.error('❌ Error deleting wine:', error);
      throw error;
    }
  },

  // Actualizar stock de un vino
  updateStock: async (wineId, quantityToReduce) => {
    try {
      console.log(`📦 Actualizando stock para vino ${wineId}, reduciendo ${quantityToReduce} unidades`);
      
      const wineRef = doc(db, 'wines', wineId);
      const wineSnap = await getDoc(wineRef);
      
      if (!wineSnap.exists()) {
        console.error(`❌ Vino no encontrado: ${wineId}`);
        throw new Error(`Vino no encontrado: ${wineId}`);
      }

      const currentWine = wineSnap.data();
      const currentStock = currentWine.stock || 0;
      const newStock = Math.max(0, currentStock - quantityToReduce);
      
      console.log(`📊 Stock actual: ${currentStock}, Nuevo stock: ${newStock}`);
      
      // Validar que hay stock suficiente
      if (currentStock < quantityToReduce) {
        console.warn(`⚠️ Stock insuficiente para ${currentWine.name}. Disponible: ${currentStock}, Solicitado: ${quantityToReduce}`);
        // Reducir solo hasta 0, no permitir stock negativo
      }

      await updateDoc(wineRef, {
        stock: newStock,
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Stock actualizado para ${currentWine.name}: ${currentStock} → ${newStock}`);
      
      return { 
        data: { 
          success: true, 
          data: { 
            id: wineId, 
            oldStock: currentStock, 
            newStock: newStock,
            reduced: quantityToReduce 
          } 
        } 
      };
    } catch (error) {
      console.error('❌ Error updating wine stock:', error);
      throw error;
    }
  },

  // Validar stock disponible para múltiples vinos
  validateStock: async (items) => {
    try {
      console.log('🔍 Validando stock para', items.length, 'productos');
      
      const stockValidations = [];
      
      for (const item of items) {
        const wineRef = doc(db, 'wines', item.wineId);
        const wineSnap = await getDoc(wineRef);
        
        if (!wineSnap.exists()) {
          stockValidations.push({
            wineId: item.wineId,
            wineName: item.wineName || 'Desconocido',
            requestedQuantity: item.quantity,
            availableStock: 0,
            valid: false,
            error: 'Producto no encontrado'
          });
          continue;
        }

        const wineData = wineSnap.data();
        const availableStock = wineData.stock || 0;
        const isValid = availableStock >= item.quantity;
        
        stockValidations.push({
          wineId: item.wineId,
          wineName: wineData.name,
          requestedQuantity: item.quantity,
          availableStock: availableStock,
          valid: isValid,
          error: isValid ? null : `Stock insuficiente. Disponible: ${availableStock}, Solicitado: ${item.quantity}`
        });
      }
      
      const allValid = stockValidations.every(validation => validation.valid);
      console.log(`📊 Validación de stock: ${allValid ? 'VÁLIDA' : 'INVÁLIDA'}`, stockValidations);
      
      return {
        data: {
          valid: allValid,
          validations: stockValidations
        }
      };
    } catch (error) {
      console.error('❌ Error validating stock:', error);
      throw error;
    }
  }
};

// Servicio para Categorías
export const firebaseCategoriesService = {
  getAll: async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('name'));
      const querySnapshot = await getDocs(q);
      
      const categories = [];
      querySnapshot.forEach((doc) => {
        categories.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return { data: { data: categories } };
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const categoryData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const categoriesRef = collection(db, 'categories');
      const docRef = await addDoc(categoriesRef, categoryData);
      
      return { 
        data: { 
          success: true, 
          data: { id: docRef.id, ...categoryData } 
        } 
      };
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const categoryRef = doc(db, 'categories', id);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp()
      };

      await updateDoc(categoryRef, updateData);
      
      return { 
        data: { 
          success: true, 
          data: { id, ...updateData } 
        } 
      };
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const categoryRef = doc(db, 'categories', id);
      await deleteDoc(categoryRef);
      
      return { data: { success: true } };
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
};

// Servicio para Bodegas
export const firebaseWineriesService = {
  getAll: async () => {
    try {
      const wineriesRef = collection(db, 'wineries');
      const q = query(wineriesRef, orderBy('name'));
      const querySnapshot = await getDocs(q);
      
      const wineries = [];
      querySnapshot.forEach((doc) => {
        wineries.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return wineries;
    } catch (error) {
      console.error('Error fetching wineries:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const wineryData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const wineriesRef = collection(db, 'wineries');
      const docRef = await addDoc(wineriesRef, wineryData);
      
      return { 
        data: { 
          success: true, 
          data: { id: docRef.id, ...wineryData } 
        } 
      };
    } catch (error) {
      console.error('Error creating winery:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const wineryRef = doc(db, 'wineries', id);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp()
      };

      await updateDoc(wineryRef, updateData);
      
      return { 
        data: { 
          success: true, 
          data: { id, ...updateData } 
        } 
      };
    } catch (error) {
      console.error('Error updating winery:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const wineryRef = doc(db, 'wineries', id);
      await deleteDoc(wineryRef);
      
      return { data: { success: true } };
    } catch (error) {
      console.error('Error deleting winery:', error);
      throw error;
    }
  }
};

// Servicio para Tipos de Vino
export const firebaseTypesService = {
  getAll: async () => {
    try {
      const typesRef = collection(db, 'types');
      const q = query(typesRef, orderBy('name'));
      const querySnapshot = await getDocs(q);
      
      const types = [];
      querySnapshot.forEach((doc) => {
        types.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return types;
    } catch (error) {
      console.error('Error fetching types:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const typeData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const typesRef = collection(db, 'types');
      const docRef = await addDoc(typesRef, typeData);
      
      return { 
        data: { 
          success: true, 
          data: { id: docRef.id, ...typeData } 
        } 
      };
    } catch (error) {
      console.error('Error creating type:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const typeRef = doc(db, 'types', id);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp()
      };

      await updateDoc(typeRef, updateData);
      
      return { 
        data: { 
          success: true, 
          data: { id, ...updateData } 
        } 
      };
    } catch (error) {
      console.error('Error updating type:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const typeRef = doc(db, 'types', id);
      await deleteDoc(typeRef);
      
      return { data: { success: true } };
    } catch (error) {
      console.error('Error deleting type:', error);
      throw error;
    }
  }
};

// Servicio para Pedidos
export const firebaseOrdersService = {
  // Obtener todos los pedidos (para admin)
  getAll: async () => {
    try {
      console.log('🔍 Firebase: Consultando todos los pedidos...');
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      console.log(`📊 Firebase: Encontrados ${querySnapshot.size} pedidos en la colección`);
      
      const orders = [];
      querySnapshot.forEach((doc) => {
        const orderData = {
          id: doc.id,
          ...doc.data()
        };
        orders.push(orderData);
        console.log(`📋 Pedido ${doc.id}:`, {
          status: orderData.status,
          total: orderData.total,
          customerEmail: orderData.customerEmail
        });
      });
      
      console.log(`✅ Firebase: Retornando ${orders.length} pedidos`);
      return { data: { data: orders } };
    } catch (error) {
      console.error('❌ Firebase: Error fetching orders:', error);
      throw error;
    }
  },

  // Obtener pedidos de un usuario específico
  getByUser: async (userId) => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const orders = [];
      querySnapshot.forEach((doc) => {
        orders.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return { data: { data: orders } };
    } catch (error) {
      console.error('Error fetching user orders:', error);
      throw error;
    }
  },

  // Crear un nuevo pedido con validación y reducción automática de stock
  create: async (orderData) => {
    try {
      console.log('🔥 Firebase: Creando nuevo pedido:', {
        customerEmail: orderData.customerEmail,
        total: orderData.total,
        items: orderData.items?.length
      });
      
      // PASO 1: Validar stock disponible antes de procesar
      console.log('📋 Paso 1: Validando stock disponible...');
      const stockValidation = await firebaseWinesService.validateStock(orderData.items);
      
      if (!stockValidation.data.valid) {
        console.error('❌ Stock insuficiente para algunos productos:', stockValidation.data.validations);
        const invalidItems = stockValidation.data.validations.filter(v => !v.valid);
        const errorMessage = `Stock insuficiente para: ${invalidItems.map(item => 
          `${item.wineName} (solicitado: ${item.requestedQuantity}, disponible: ${item.availableStock})`
        ).join(', ')}`;
        
        throw new Error(errorMessage);
      }
      
      console.log('✅ Validación de stock exitosa, todos los productos están disponibles');
      
      // PASO 2: Crear el pedido
      console.log('📋 Paso 2: Creando pedido...');
      const cleanOrderData = Object.fromEntries(
        Object.entries(orderData).filter(([_, v]) => v !== undefined)
      );
      const newOrder = {
        ...cleanOrderData,
        status: orderData.status || 'pending',
        paymentStatus: orderData.paymentStatus || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const ordersRef = collection(db, 'orders');
      const docRef = await addDoc(ordersRef, newOrder);
      
      console.log('✅ Pedido creado con ID:', docRef.id);
      
      // PASO 3: Reducir stock para cada producto
      console.log('📋 Paso 3: Reduciendo stock automáticamente...');
      const stockUpdateResults = [];
      
      for (const item of orderData.items) {
        try {
          console.log(`📦 Reduciendo stock para ${item.wineName}: ${item.quantity} unidades`);
          const stockUpdate = await firebaseWinesService.updateStock(item.wineId, item.quantity);
          stockUpdateResults.push({
            wineId: item.wineId,
            wineName: item.wineName,
            success: true,
            oldStock: stockUpdate.data.data.oldStock,
            newStock: stockUpdate.data.data.newStock,
            reduced: stockUpdate.data.data.reduced
          });
          console.log(`✅ Stock actualizado para ${item.wineName}: ${stockUpdate.data.data.oldStock} → ${stockUpdate.data.data.newStock}`);
        } catch (error) {
          console.error(`❌ Error actualizando stock para ${item.wineName}:`, error);
          stockUpdateResults.push({
            wineId: item.wineId,
            wineName: item.wineName,
            success: false,
            error: error.message
          });
        }
      }
      
      console.log('📊 Resumen de actualizaciones de stock:', stockUpdateResults);
      
      // PASO 4: Agregar información del stock al pedido
      const finalOrder = {
        ...newOrder,
        stockUpdates: stockUpdateResults,
        stockProcessed: true
      };
      
      // Actualizar el pedido con la información de stock
      await updateDoc(doc(db, 'orders', docRef.id), {
        stockUpdates: stockUpdateResults,
        stockProcessed: true
      });
      
      console.log('✅ Firebase: Pedido guardado exitosamente con reducción de stock automática');
      
      return { 
        data: { 
          success: true, 
          data: { id: docRef.id, ...finalOrder } 
        } 
      };
    } catch (error) {
      console.error('❌ Firebase: Error creating order:', error);
      throw error;
    }
  },

  // Obtener un pedido por ID
  getById: async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('Pedido no encontrado');
      }

      return {
        data: {
          data: {
            id: orderSnap.id,
            ...orderSnap.data()
          }
        }
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  // Actualizar estado de pedido
  updateStatus: async (orderId, status) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const updateData = {
        status,
        updatedAt: serverTimestamp()
      };

      await updateDoc(orderRef, updateData);
      
      return { 
        data: { 
          success: true, 
          data: { id: orderId, status } 
        } 
      };
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  // Obtener estadísticas de pedidos
  getStats: async () => {
    try {
      console.log('🔍 Firebase: Consultando estadísticas de pedidos...');
      const ordersRef = collection(db, 'orders');
      const querySnapshot = await getDocs(ordersRef);
      
      console.log(`📊 Firebase: Encontrados ${querySnapshot.size} documentos en la colección orders`);
      
      let totalOrders = 0;
      let pendingOrders = 0;
      let completedOrders = 0;
      let totalRevenue = 0;

      querySnapshot.forEach((doc) => {
        const order = doc.data();
        console.log(`📋 Procesando pedido ${doc.id}:`, {
          status: order.status,
          total: order.total,
          customerEmail: order.customerEmail || order.shippingAddress?.email,
          customerName: order.customerName || order.shippingAddress?.name
        });
        
        if (order.paymentStatus !== 'paid') {
          return;
        }

        totalOrders++;

        if (order.status === 'pending' || order.status === 'confirmed') {
          pendingOrders++;
        } else if (order.status === 'delivered') {
          completedOrders++;
        }

        const orderTotal = parseFloat(order.total) || 0;
        if (!isNaN(orderTotal) && orderTotal > 0) {
          totalRevenue += orderTotal;
          console.log(`💰 Sumando ${orderTotal} al total de ingresos, acumulado: ${totalRevenue}`);
        } else {
          console.warn(`⚠️ Pedido ${doc.id} tiene total inválido:`, order.total);
        }
      });
      
      const stats = {
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue: Math.round(totalRevenue * 100) / 100
      };
      
      console.log('📈 Estadísticas finales calculadas:', stats);
      
      return {
        data: {
          data: stats
        }
      };
    } catch (error) {
      console.error('❌ Error fetching order stats:', error);
      throw error;
    }
  },

  // Eliminar un pedido (solo admin)
  delete: async (orderId) => {
    try {
      console.log('🗑️ Firebase: Eliminando pedido...', orderId);
      
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('Pedido no encontrado');
      }

      const orderData = orderSnap.data();
      console.log('📋 Pedido a eliminar:', {
        id: orderId,
        customerEmail: orderData.customerEmail || orderData.shippingAddress?.email,
        total: orderData.total,
        status: orderData.status
      });

      await deleteDoc(orderRef);
      
      console.log('✅ Pedido eliminado de Firebase:', orderId);
      
      return { 
        data: { 
          success: true, 
          message: 'Pedido eliminado exitosamente'
        } 
      };
    } catch (error) {
      console.error('❌ Error deleting order:', error);
      throw error;
    }
  }
};

// Helper para inicializar datos de ejemplo (solo para desarrollo)
export const initializeFirebaseData = async () => {
  try {
    // Verificar si ya hay datos
    const categoriesSnapshot = await getDocs(collection(db, 'categories'));
    if (!categoriesSnapshot.empty) {
      console.log('Firebase data already initialized');
      return;
    }

    console.log('Initializing Firebase with sample data...');

    // Crear categorías
    const categories = [
      { name: 'Rioja', description: 'Vinos de la región de La Rioja' },
      { name: 'Ribera del Duero', description: 'Vinos de Ribera del Duero' },
      { name: 'Rías Baixas', description: 'Vinos de las Rías Baixas gallegas' },
      { name: 'Penedès', description: 'Vinos y cavas del Penedès' },
      { name: 'Jumilla', description: 'Vinos de la región de Jumilla' }
    ];

    for (const category of categories) {
      await firebaseCategoriesService.create(category);
    }

    // Crear bodegas
    const wineries = [
      { name: 'Marqués de Riscal', region: 'Rioja', founded: 1858, description: 'Una de las bodegas más prestigiosas de la Rioja' },
      { name: 'Vega Sicilia', region: 'Ribera del Duero', founded: 1864, description: 'Icónica bodega de Ribera del Duero' },
      { name: 'Codorníu', region: 'Penedès', founded: 1551, description: 'Pioneros en la elaboración de cava' },
      { name: 'Rías Baixas Albariño', region: 'Galicia', founded: 1988, description: 'Especialistas en vinos albariño' }
    ];

    for (const winery of wineries) {
      await firebaseWineriesService.create(winery);
    }

    // Crear tipos
    const types = [
      { name: 'Tinto', color: 'error', description: 'Vinos tintos elaborados con uvas rojas' },
      { name: 'Blanco', color: 'warning', description: 'Vinos blancos elaborados con uvas blancas' },
      { name: 'Rosado', color: 'secondary', description: 'Vinos rosados con color intermedio' },
      { name: 'Espumoso', color: 'info', description: 'Vinos espumosos y cavas' }
    ];

    for (const type of types) {
      await firebaseTypesService.create(type);
    }

    console.log('Firebase data initialized successfully');
  } catch (error) {
    console.error('Error initializing Firebase data:', error);
  }
};

// Función de debugging para probar Firebase
export const debugFirebaseOrders = async () => {
  try {
    console.log('🧪 DEBUGGING FIREBASE ORDERS:');
    
    // 1. Probar creación de pedido
    console.log('1️⃣ Creando pedido de prueba...');
    const testOrder = {
      customerName: 'Test User',
      customerEmail: 'test@debug.com',
      total: 99.99,
      subtotal: 89.99,
      shipping: 10.00,
      isGuest: true,
      items: [
        {
          wineId: 'test-wine-1',
          wineName: 'Test Wine',
          price: 89.99,
          quantity: 1
        }
      ],
      shippingAddress: {
        name: 'Test User',
        email: 'test@debug.com',
        phone: '123456789',
        street: 'Test Street 123',
        city: 'Test City',
        postalCode: '12345'
      },
      paymentMethod: 'stripe'
    };
    
    const createResult = await firebaseOrdersService.create(testOrder);
    console.log('✅ Pedido creado:', createResult);
    
    // 2. Probar consulta de todos los pedidos
    console.log('2️⃣ Consultando todos los pedidos...');
    const allOrders = await firebaseOrdersService.getAll();
    console.log('📋 Todos los pedidos:', allOrders);
    
    // 3. Probar estadísticas
    console.log('3️⃣ Consultando estadísticas...');
    const stats = await firebaseOrdersService.getStats();
    console.log('📊 Estadísticas:', stats);
    
    return {
      success: true,
      testOrderId: createResult.data.data.id,
      totalOrders: allOrders.data.data.length,
      stats: stats.data.data
    };
    
  } catch (error) {
    console.error('❌ Error en debugging:', error);
    throw error;
  }
};

// Función para limpiar pedidos de prueba
export const cleanupTestOrders = async () => {
  try {
    console.log('🧹 Limpiando pedidos de prueba...');
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, where('customerEmail', '==', 'test@debug.com'));
    const querySnapshot = await getDocs(q);
    
    const deletePromises = [];
    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    await Promise.all(deletePromises);
    console.log(`✅ ${deletePromises.length} pedidos de prueba eliminados`);
    
    return { deleted: deletePromises.length };
  } catch (error) {
    console.error('❌ Error limpiando pedidos:', error);
    throw error;
  }
};

// Función para eliminar todos los pedidos de prueba (incluyendo otros emails de prueba)
export const cleanupAllTestOrders = async () => {
  try {
    console.log('🧹 Limpiando TODOS los pedidos de prueba...');
    const ordersResponse = await firebaseOrdersService.getAll();
    const orders = ordersResponse.data.data;
    
    const testOrders = orders.filter(order => {
      const email = order.customerEmail || order.shippingAddress?.email || '';
      const name = order.customerName || order.shippingAddress?.name || '';
      
      return email.toLowerCase().includes('test') || 
             email.toLowerCase().includes('prueba') ||
             email.toLowerCase().includes('debug') ||
             name.toLowerCase().includes('test') ||
             name.toLowerCase().includes('prueba');
    });
    
    console.log(`🗑️ Encontrados ${testOrders.length} pedidos de prueba para eliminar`);
    
    const deletePromises = testOrders.map(order => {
      console.log(`🗑️ Eliminando: ${order.customerEmail || order.shippingAddress?.email} (${order.id})`);
      return firebaseOrdersService.delete(order.id);
    });
    
    await Promise.all(deletePromises);
    console.log(`✅ ${testOrders.length} pedidos de prueba eliminados`);
    
    return { deleted: testOrders.length };
  } catch (error) {
    console.error('❌ Error limpiando pedidos de prueba:', error);
    throw error;
  }
};

// Función para testear específicamente las estadísticas
export const testOrderStats = async () => {
  try {
    console.log('🧪 TESTING ORDER STATS:');
    
    // 1. Consultar todos los pedidos
    console.log('1️⃣ Consultando todos los pedidos...');
    const allOrdersResponse = await firebaseOrdersService.getAll();
    const orders = allOrdersResponse.data.data;
    console.log(`📊 Total de pedidos encontrados: ${orders.length}`);
    
    // 2. Mostrar estructura de cada pedido
    orders.forEach((order, index) => {
      console.log(`📋 Pedido ${index + 1}:`, {
        id: order.id,
        customerName: order.customerName,
        customerEmail: order.customerEmail,
        shippingName: order.shippingAddress?.name,
        shippingEmail: order.shippingAddress?.email,
        total: order.total,
        status: order.status,
        createdAt: order.createdAt
      });
    });
    
    // 3. Consultar estadísticas
    console.log('3️⃣ Consultando estadísticas...');
    const statsResponse = await firebaseOrdersService.getStats();
    console.log('📈 Respuesta completa de estadísticas:', statsResponse);
    console.log('📈 Estadísticas extraídas:', statsResponse.data.data);
    
    return {
      ordersCount: orders.length,
      orders: orders,
      stats: statsResponse.data.data
    };
    
  } catch (error) {
    console.error('❌ Error en test de estadísticas:', error);
    throw error;
  }
};

// Función para crear pedidos de prueba para testing
export const createTestOrders = async () => {
  try {
    console.log('🧪 Creando pedidos de prueba...');
    
    const testOrders = [
      {
        customerName: 'Juan Pérez',
        customerEmail: 'juan@test.com',
        total: 45.99,
        subtotal: 39.99,
        shipping: 6.00,
        isGuest: true,
        status: 'pending',
        items: [
          { wineId: 'test-1', wineName: 'Vino Test Rioja', price: 39.99, quantity: 1 }
        ],
        shippingAddress: {
          name: 'Juan Pérez',
          email: 'juan@test.com',
          phone: '123456789',
          street: 'Calle Test 123',
          city: 'Madrid',
          postalCode: '28001'
        },
        paymentMethod: 'stripe'
      },
      {
        customerName: 'María García',
        customerEmail: 'maria@test.com',
        total: 89.98,
        subtotal: 79.98,
        shipping: 9.99,
        isGuest: false,
        userId: 'user-test-123',
        status: 'delivered',
        items: [
          { wineId: 'test-2', wineName: 'Vino Test Ribera', price: 39.99, quantity: 2 }
        ],
        shippingAddress: {
          name: 'María García',
          email: 'maria@test.com',
          phone: '987654321',
          street: 'Avenida Test 456',
          city: 'Barcelona',
          postalCode: '08001'
        },
        paymentMethod: 'stripe'
      },
      {
        customerName: 'Pedro López',
        customerEmail: 'pedro@test.com',
        total: 120.50,
        subtotal: 110.50,
        shipping: 10.00,
        isGuest: true,
        status: 'pending',
        items: [
          { wineId: 'test-3', wineName: 'Vino Test Premium', price: 55.25, quantity: 2 }
        ],
        shippingAddress: {
          name: 'Pedro López',
          email: 'pedro@test.com',
          phone: '555123456',
          street: 'Plaza Test 789',
          city: 'Valencia',
          postalCode: '46001'
        },
        paymentMethod: 'stripe'
      }
    ];

    const results = [];
    
    for (const testOrder of testOrders) {
      console.log(`📝 Creando pedido para ${testOrder.customerName}...`);
      const result = await firebaseOrdersService.create(testOrder);
      results.push(result);
      console.log(`✅ Pedido creado con ID: ${result.data.data.id}`);
    }

    console.log(`🎉 ${results.length} pedidos de prueba creados exitosamente`);
    
    // Verificar estadísticas después de crear los pedidos
    const statsAfter = await firebaseOrdersService.getStats();
    console.log('📊 Estadísticas después de crear pedidos:', statsAfter.data.data);
    
    return {
      created: results.length,
      orders: results,
      stats: statsAfter.data.data
    };
    
  } catch (error) {
    console.error('❌ Error creando pedidos de prueba:', error);
    throw error;
  }
};

// Función para testear específicamente los vinos
export const testWineManagement = async () => {
  try {
    console.log('🧪 TESTING WINE MANAGEMENT:');
    
    // 1. Consultar todos los vinos
    console.log('1️⃣ Consultando todos los vinos...');
    const allWinesResponse = await firebaseWinesService.getAll();
    const wines = allWinesResponse.data.data;
    console.log(`🍷 Total de vinos encontrados: ${wines.length}`);
    
    // 2. Mostrar estructura de cada vino
    wines.forEach((wine, index) => {
      console.log(`🍷 Vino ${index + 1}:`, {
        id: wine.id,
        name: wine.name,
        price: wine.price,
        categoryId: wine.categoryId,
        wineryId: wine.wineryId,
        type: wine.type,
        imageUrl: wine.imageUrl,
        createdAt: wine.createdAt
      });
    });
    
    // 3. Consultar datos relacionados
    console.log('3️⃣ Consultando datos relacionados...');
    const categoriesResponse = await firebaseCategoriesService.getAll();
    const categories = categoriesResponse.data.data;
    console.log(`📁 Categorías: ${categories.length}`);
    
    const wineries = await firebaseWineriesService.getAll();
    console.log(`🏭 Bodegas: ${wineries.length}`);
    
    const types = await firebaseTypesService.getAll();
    console.log(`🍾 Tipos: ${types.length}`);
    
    return {
      winesCount: wines.length,
      wines: wines,
      categoriesCount: categories.length,
      wineriesCount: wineries.length,
      typesCount: types.length
    };
    
  } catch (error) {
    console.error('❌ Error en test de gestión de vinos:', error);
    throw error;
  }
};

// Función para crear un vino de prueba (sin imagen real)
export const createTestWine = async () => {
  try {
    console.log('🧪 Creando vino de prueba...');
    
    // Crear FormData simulado (sin imagen real)
    const testFormData = new FormData();
    testFormData.append('name', 'Vino de Prueba Firebase');
    testFormData.append('description', 'Este es un vino de prueba creado para verificar el funcionamiento de Firebase Storage y Firestore');
    testFormData.append('price', '29.99');
    testFormData.append('categoryId', 'cat-rioja'); // Asumiendo que existe
    testFormData.append('wineryId', 'winery-marques-riscal'); // Asumiendo que existe
    testFormData.append('type', 'Tinto');
    testFormData.append('year', '2021');
    testFormData.append('alcoholContent', '13.5');
    testFormData.append('stock', '25');
    
    console.log('📝 Datos del vino de prueba preparados');
    
    // Crear el vino
    const result = await firebaseWinesService.create(testFormData);
    console.log('✅ Vino de prueba creado exitosamente:', result.data.data.id);
    
    return result.data.data;
    
  } catch (error) {
    console.error('❌ Error creando vino de prueba:', error);
    throw error;
  }
};

// Función para limpiar vinos de prueba
export const cleanupTestWines = async () => {
  try {
    console.log('🧹 Limpiando vinos de prueba...');
    const winesResponse = await firebaseWinesService.getAll();
    const wines = winesResponse.data.data;
    
    const testWines = wines.filter(wine => 
      wine.name.toLowerCase().includes('prueba') || 
      wine.name.toLowerCase().includes('test')
    );
    
    console.log(`🗑️ Encontrados ${testWines.length} vinos de prueba para eliminar`);
    
    const deletePromises = testWines.map(wine => {
      console.log(`🗑️ Eliminando: ${wine.name} (${wine.id})`);
      return firebaseWinesService.delete(wine.id);
    });
    
    await Promise.all(deletePromises);
    console.log(`✅ ${testWines.length} vinos de prueba eliminados`);
    
    return { deleted: testWines.length };
  } catch (error) {
    console.error('❌ Error limpiando vinos de prueba:', error);
    throw error;
  }
}; 